import { ledeHtml } from './rich-text.js';
import { localeHref, localizeBodyHrefs } from './locale-href.js';
function esc(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/** BCP 47 tag for Intl in each page language. */
const INTL_TAG = {
    en: 'en-US', ru: 'ru-RU', es: 'es-ES', pt: 'pt-BR', tr: 'tr-TR', zh: 'zh-CN',
};
/** A post or page date in the page's language ("30 сент. 2026 г."); ISO stays in `datetime`. */
export function formatPostDate(iso, locale) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso))
        return iso;
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime()))
        return iso;
    return new Intl.DateTimeFormat(INTL_TAG[locale] ?? 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(d);
}
/** Every country h1 in every language reads "{name} eSIM" + ": " / " — " / "：" + tagline. */
const TITLE_PARTS = /^(.*?eSIM)(\s*[—–:：]\s*)(.+)$/;
/** The h1's inner HTML: the name large, the tagline on its own line. */
export function countryTitleHtml(h1) {
    const m = TITLE_PARTS.exec(h1);
    if (!m)
        return `<span class="country-hero__name">${esc(h1)}</span>`;
    return `<span class="country-hero__name">${esc(m[1] ?? '')}</span>`
        + `<span class="country-hero__sep">${esc(m[2] ?? '')}</span>`
        + `<span class="country-hero__tagline">${esc(m[3] ?? '')}</span>`;
}
/**
 * A photo's credit as inner HTML: Unsplash names and links the photographer
 * and the photo, the attribution its licence asks for. Country and blog
 * heroes share it.
 */
export function photoCreditHtml(hero) {
    const unsplash = /^Photo by (.+?) on Unsplash$/.exec(hero.credit ?? '');
    const link = (href, text) => href ? `<a href="${esc(href)}" rel="noopener noreferrer" target="_blank">${esc(text)}</a>` : esc(text);
    if (unsplash)
        return `Photo by ${link(hero.photographerUrl, unsplash[1] ?? '')} on ${link(hero.sourceUrl, 'Unsplash')}`;
    if (!hero.credit && !hero.license)
        return '';
    return [hero.credit ? esc(hero.credit) : '', hero.license ? esc(hero.license) : '', hero.sourceUrl ? link(hero.sourceUrl, 'source') : '']
        .filter(Boolean).join(' · ');
}
/** `srcset` for a hero: the 800w and 1200w variants beside the 1600w original. */
export function heroSrcset(src) {
    const base = src.replace(/\.webp$/, '');
    return `${base}-800w.webp 800w, ${base}-1200w.webp 1200w, ${src} 1600w`;
}
/** The photo with its credit. */
export function countryHeroMediaHtml(hero) {
    const credit = photoCreditHtml(hero);
    return `<figure class="country-hero__media">
        <img class="country-hero__media-img" src="${esc(hero.src)}" srcset="${esc(heroSrcset(hero.src))}" sizes="(max-width: 768px) calc(100vw - 64px), 1216px" alt="${esc(hero.alt)}" width="${hero.width}" height="${hero.height}" loading="eager" fetchpriority="high" decoding="async" />
        ${credit ? `<figcaption class="country-hero__media-credit">${credit}</figcaption>` : ''}
      </figure>`;
}
/** `.country-hero`: photo, kicker, heading, rate, lede, date, buy button, note. */
export function countryHeroHtml(i) {
    const updated = i.lastUpdated
        ? `<p class="country-hero__updated"><time datetime="${esc(i.lastUpdated)}">${esc(i.s.lastUpdated)}: ${esc(formatPostDate(i.lastUpdated, i.locale))}</time></p>`
        : '';
    const premium = i.isPremium
        ? `<span class="chip chip--warn" title="${esc(i.s.premiumTooltip)}">${esc(i.s.premiumLabel)}</span>`
        : '';
    return `<div class="country-hero${i.hero ? ' country-hero--with-media' : ''}">
      ${i.hero ? countryHeroMediaHtml(i.hero) : ''}
      <div class="country-hero__content">
        <div class="section__kicker country-hero__kicker">${esc(i.s.kicker)}</div>
        <div class="country-hero__title-row">
          <span class="country-hero__flag" aria-hidden="true">${esc(i.flag)}</span>
          <h1 class="country-hero__title">${countryTitleHtml(i.h1)}</h1>
        </div>
        ${premium}
        <div class="country-hero__rate">
          <div class="hero__caption"><span class="dot"></span>${esc(i.s.perMb)}<span class="dot"></span></div>
          <div class="hero__number"><span class="currency">$</span><span>${esc(i.rateMb)}</span><span class="unit">/ MB</span></div>
        </div>
        ${i.lede ? `<p class="country-hero__lede">${ledeHtml(i.lede)}</p>` : ''}
        ${updated}
        <button type="button" class="btn btn--accent country-hero__cta" data-action="get-esim" data-country-slug="${esc(i.slug)}" data-country-name="${esc(i.name)}" data-country-flag="${esc(i.flag)}">${esc(i.s.buyCta)}</button>
        <p class="country-hero__global-note">${esc(i.s.globalNote)}</p>
      </div>
    </div>`;
}
/** `.country-faq`: the article's questions in the /faq accordion. */
export function countryFaqHtml(kicker, heading, faq) {
    if (faq.length === 0)
        return '';
    const items = faq.map((p) => `<details class="faq-item"><summary class="faq-item__q faq-question">${esc(p.q)}</summary><div class="faq-item__a faq-answer">${esc(p.a)}</div></details>`).join('');
    return `<section class="country-faq">
    <div class="container">
      <div class="section__kicker country-faq__kicker">${esc(kicker)}</div>
      <h2 class="country-faq__heading">${esc(heading)}</h2>
      <div class="country-faq__items faq-list">${items}</div>
    </div>
  </section>`;
}
/**
 * Turkish possessive-ablative suffix for a percentage — «%84'ünden».
 *
 * The template froze «'sinden», which only fits numbers whose spoken form ends
 * like «iki/yedi/yirmi/elli». 84 is «seksen dört», so the correct chain is
 * dört → dördü → dördünden, written %84'ünden — the frozen form produced a
 * shape no Turkish writer would («%84'sinden», audit 2026-07-31). Vowel
 * harmony follows the LAST spoken word of the numeral: the last non-zero digit,
 * or the tens/hundreds word when the number ends in zero.
 */
export function trPctSuffix(pct) {
    const d = pct % 10;
    if (d !== 0) {
        return { 1: "'inden", 2: "'sinden", 3: "'ünden", 4: "'ünden", 5: "'inden", 6: "'sından", 7: "'sinden", 8: "'inden", 9: "'undan" }[d];
    }
    const t = pct % 100;
    if (t !== 0) {
        return { 10: "'undan", 20: "'sinden", 30: "'undan", 40: "'ından", 50: "'sinden", 60: "'ından", 70: "'inden", 80: "'inden", 90: "'ından" }[t];
    }
    return "'ünden"; // yüz
}
/**
 * Rank by per-MB rate (1 = cheapest), the last place of the countries sharing
 * that rate, and the share of the catalog that costs more.
 *
 * 157 of 193 countries share their rate with others (31 at the lowest), so a
 * bare "#1 of 193, cheaper than 84%" read as a contradiction on 80% of pages.
 * `rankEnd > rank` means a tie; the page then names the range (2026-09-30).
 */
export function priceRank(rows, slug) {
    const me = rows.find((c) => c.slug === slug);
    if (!me || rows.length < 2)
        return null;
    const cheaper = rows.filter((c) => c.rateUsdtPerMb < me.rateUsdtPerMb).length;
    const same = rows.filter((c) => c.rateUsdtPerMb === me.rateUsdtPerMb).length;
    const moreExpensive = rows.filter((c) => c.rateUsdtPerMb > me.rateUsdtPerMb).length;
    return { rank: cheaper + 1, rankEnd: cheaper + same, total: rows.length, pct: Math.round((moreExpensive / (rows.length - 1)) * 100) };
}
/**
 * Neighbouring countries to link from a country page: same zone first, then
 * the rest, each by closeness of rate.
 *
 * Ten since 2026-08-05. GSC showed the country pages, the ones that carry our
 * impressions, at the bottom of the internal link graph: one inbound link each
 * from /prices, while /legal/terms collected 1105 from the footer.
 */
export function similarCountries(rows, slug, n = 10) {
    const me = rows.find((c) => c.slug === slug);
    if (!me)
        return [];
    const others = rows.filter((c) => c.slug !== slug);
    const byRateProximity = (a, b) => Math.abs(a.rateUsdtPerMb - me.rateUsdtPerMb) - Math.abs(b.rateUsdtPerMb - me.rateUsdtPerMb);
    const zone = others.filter((c) => c.zone && me.zone && c.zone === me.zone).sort(byRateProximity);
    if (zone.length >= n)
        return zone.slice(0, n);
    const picked = new Set(zone.map((c) => c.slug));
    const topUp = others.filter((c) => !picked.has(c.slug)).sort(byRateProximity);
    return [...zone, ...topUp].slice(0, n);
}
/** djb2: stable across builds, so a page links the same countries on every render. */
function slugHash(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++)
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}
/**
 * The "Other destinations" rail: `count` slugs picked by a hash of the current
 * one and a prime step, so every country links a different set and every
 * country gets ~6 inbound links (operator SEO finding 2026-05-09: the first
 * version linked the same alphabetical six from all 184 pages).
 */
export function relatedCountrySlugs(slugs, current, count = 6) {
    const all = slugs.filter((s) => s !== current);
    if (all.length <= count)
        return all;
    const seed = slugHash(current);
    const picks = [];
    const seen = new Set();
    for (let i = 0; i < count; i++) {
        let idx = (seed + i * 31) % all.length;
        let candidate = all[idx];
        if (seen.has(candidate)) {
            for (let j = 1; j < all.length; j++) {
                idx = (idx + 1) % all.length;
                candidate = all[idx];
                if (!seen.has(candidate))
                    break;
            }
        }
        picks.push(candidate);
        seen.add(candidate);
    }
    return picks;
}
/**
 * Upcoming events a visitor to this country might be travelling for (added
 * 2026-08-17). The event guides were linked only from the events calendar post,
 * so the country page a traveller lands on never pointed at them. Dates are the
 * real end dates; an entry drops off by itself once its event is over.
 * EN-only on purpose: the event guides themselves are English.
 */
export const COUNTRY_EVENTS = {
    'esim-germany': [
        { slug: 'esim-for-oktoberfest', title: 'Oktoberfest', ends: '2026-10-04' },
        { slug: 'esim-for-berlin-marathon', title: 'the Berlin Marathon', ends: '2026-09-27' },
    ],
    'esim-singapore': [
        { slug: 'esim-for-f1-singapore-grand-prix', title: 'the Singapore Grand Prix', ends: '2026-10-11' },
    ],
    'esim-china': [
        { slug: 'esim-for-canton-fair', title: 'the Canton Fair', ends: '2026-11-04' },
    ],
    'esim-portugal': [
        { slug: 'esim-for-web-summit', title: 'Web Summit', ends: '2026-11-12' },
    ],
    'esim-turkey': [
        { slug: 'esim-for-cop31-antalya', title: 'COP31 in Antalya', ends: '2026-11-20' },
    ],
    'esim-united-arab-emirates': [
        { slug: 'esim-for-gitex-dubai', title: 'GITEX Dubai', ends: '2026-12-11' },
    ],
};
function breadcrumbsHtml(i) {
    const href = (p) => esc(localeHref(i.locale, p));
    return `<div class="breadcrumbs">
        <a href="${href('/')}">${esc(i.t('country.breadcrumbsHome'))}</a> · <a href="${href('/prices')}">${esc(i.t('country.breadcrumbsPrices'))}</a> · <span><span aria-hidden="true">${esc(i.country.flag ?? '')}</span> ${esc(i.country.name)}</span>
      </div>`;
}
function bodyHtml(i) {
    if (!i.article?.body)
        return '';
    // Curated HTML from the catalogs, rendered as-is; only its links get the locale.
    return `<section class="country-article">
    <div class="container">
      <div class="country-article__body article-body">
        ${localizeBodyHrefs(i.article.body, i.locale, { hasBlogTranslation: i.hasBlogTranslation })}
      </div>
    </div>
  </section>`;
}
function factsHtml(i) {
    const { t, facts, locale, country } = i;
    const tag = INTL_TAG[locale] ?? 'en-US';
    const nf = new Intl.NumberFormat(tag);
    const rows = [];
    const row = (key, value) => { rows.push(`<tr><th>${esc(t(key))}</th><td>${esc(value)}</td></tr>`); };
    if (facts.capital)
        row('country.facts.capital', facts.capital);
    if (facts.currency) {
        let cur = facts.currency;
        try {
            const dn = new Intl.DisplayNames(tag, { type: 'currency' }).of(facts.currency);
            if (dn && dn !== facts.currency)
                cur = `${dn} (${facts.currency})`;
        }
        catch { /* keep the code */ }
        row('country.facts.currency', cur);
    }
    if (facts.languages?.length)
        row('country.facts.languages', facts.languages.slice(0, 4).join(', '));
    if (typeof facts.population === 'number')
        row('country.facts.population', nf.format(facts.population));
    if (typeof facts.area_km2 === 'number')
        row('country.facts.area', `${nf.format(facts.area_km2)} km²`);
    const rank = priceRank(i.countries, country.slug);
    const rankHtml = rank
        ? `<p class="country-facts__rank">${esc(t(rank.rankEnd > rank.rank ? 'country.facts.priceRankTied' : 'country.facts.priceRank', {
            rank: nf.format(rank.rank), rankEnd: nf.format(rank.rankEnd), total: nf.format(rank.total), pct: nf.format(rank.pct), trPctSuffix: trPctSuffix(rank.pct),
        }))}</p>`
        : '';
    const similar = similarCountries(i.countries, country.slug);
    const simHtml = similar.length
        ? `<h3 class="country-facts__similar-heading">${esc(t('country.facts.similarHeading'))}</h3>
      <ul class="article-list country-facts__similar">${similar.map((c) => `<li><a href="${esc(localeHref(locale, `/countries/${c.slug}`))}">${esc(c.flag ? `${c.flag} ` : '')}${esc(c.name)}</a> — $${c.rateUsdtPerMb.toFixed(4)}/MB</li>`).join('')}</ul>`
        : '';
    if (rows.length === 0 && !rankHtml && !simHtml)
        return '';
    return `<section class="country-facts">
    <div class="container">
      <h2 class="article-h2">${esc(t('country.facts.heading', { country: country.name }))}</h2>
      ${rows.length ? `<table class="article-table country-facts__table"><tbody>${rows.join('')}</tbody></table>` : ''}
      ${rankHtml}
      ${simHtml}
    </div>
  </section>`;
}
function eventsHtml(i) {
    if (i.locale !== 'en')
        return '';
    const live = (COUNTRY_EVENTS[i.country.slug] ?? []).filter((e) => e.ends >= i.today);
    if (live.length === 0)
        return '';
    const items = live.map((e) => `<li><a href="/blog/${esc(e.slug)}">Travelling for ${esc(e.title)}? Read the connectivity guide</a></li>`).join('');
    return `<section class="country-events">
    <div class="container"><ul class="country-events__list">${items}</ul></div>
  </section>`;
}
function relatedHtml(i) {
    const bySlug = new Map(i.countries.map((c) => [c.slug, c]));
    const cards = relatedCountrySlugs(i.articleSlugs, i.country.slug)
        .map((s) => bySlug.get(s))
        .filter((c) => !!c)
        .map((c) => `<a class="related-card" href="${esc(localeHref(i.locale, `/countries/${c.slug}`))}">
          <div class="related-card__cat"><span aria-hidden="true">${esc(c.flag ?? '')}</span> ${esc(c.name)}</div>
          <div class="related-card__title">${esc(c.name)} eSIM</div>
          <div class="related-card__excerpt related-card__excerpt--rate">
            <span class="related-card__rate-primary mono">$${c.rateUsdtPerMb.toFixed(4)}/MB</span>
            <span class="related-card__rate-ref mono">≈ $${c.rateUsdtPerGb.toFixed(2)}/GB</span>
          </div>
        </a>`)
        .join('');
    if (!cards)
        return '';
    return `<section class="section country-related">
    <div class="container">
      <div class="section__kicker">${esc(i.t('country.otherDestinations'))}</div>
      <div class="related-grid">${cards}</div>
    </div>
  </section>`;
}
/**
 * Everything inside `<article class="country-route">`: hero with breadcrumbs,
 * article body, free-eSIM card, facts and similar countries, event links, FAQ,
 * other destinations.
 */
export function countryPageHtml(i) {
    const { t, country, article } = i;
    const hero = countryHeroHtml({
        slug: country.slug,
        flag: country.flag ?? '',
        name: country.name,
        h1: article?.h1 ?? `${country.name} eSIM`,
        lede: article?.lede ?? '',
        lastUpdated: article?.lastUpdated ?? '',
        locale: i.locale,
        rateMb: country.rateUsdtPerMb.toFixed(4),
        isPremium: country.isPremium === true,
        hero: article?.hero ?? null,
        s: {
            kicker: t('country.esimIn'),
            perMb: t('country.perMb'),
            buyCta: t('country.buyCta', { country: country.name }),
            globalNote: t('country.globalNote', { country: country.name }),
            lastUpdated: t('article.lastUpdated'),
            premiumLabel: t('country.premium.label'),
            premiumTooltip: t('country.premium.tooltip'),
        },
    });
    return `
  <section class="country-hero-section">
    <div class="container">
      ${breadcrumbsHtml(i)}
      ${hero}
    </div>
  </section>
  ${bodyHtml(i)}
  ${i.trialCtaHtml}
  ${factsHtml(i)}
  ${eventsHtml(i)}
  ${countryFaqHtml(t('country.faq.kicker'), t('country.faq.heading', { country: country.name }), article?.faq ?? [])}
  ${relatedHtml(i)}
`;
}
